import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import {
  FetchROIDashboardDataRequest,
  FetchROIDashboardDataResponse,
  DashboardTables,
  LastSyncInfo,
  DashboardMetrics,
  DashboardCharts,
  ROICardData,
} from "../api/fetchROIDashboardData/types";
import { fetchROIDashboardData } from "../api/fetchROIDashboardData/fetchROIDashboardDataApi";
import { refreshROIDashboardData } from "../api/refreshROIDashboardData/refreshROIDashboardDataApi";

interface ROIDashboardState {
  dashboardData: {
    tables: DashboardTables | null;
    roiCard: ROICardData | null;
    metrics: DashboardMetrics | null;
    charts: DashboardCharts | null;
    lastSync: LastSyncInfo | null;
  };
  loading: boolean;
  refreshing: boolean;
  error: string | null;
}

const initialState: ROIDashboardState = {
  dashboardData: {
    tables: null,
    roiCard: null,
    metrics: null,
    charts: null,
    lastSync: null,
  },
  loading: false,
  refreshing: false,
  error: null,
};

const roiDashboardSlice = createSlice({
  name: "roiDashboard",
  initialState,
  reducers: {
    setDashboardData: (
      state,
      action: PayloadAction<ROIDashboardState["dashboardData"]>,
    ) => {
      state.dashboardData = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setRefreshing: (state, action: PayloadAction<boolean>) => {
      state.refreshing = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const { setDashboardData, setLoading, setRefreshing, setError } =
  roiDashboardSlice.actions;

export const fetchROIDashboardDataAction =
  (requestData: FetchROIDashboardDataRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response: FetchROIDashboardDataResponse =
        await fetchROIDashboardData(requestData);
      dispatch(
        setDashboardData({
          tables: response.data?.tables || null,
          roiCard: response.data?.roiCard || null,
          metrics: response.data?.metrics || null,
          charts: response.data?.charts || null,
          lastSync: response.data?.lastSync || null,
        }),
      );
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch ROI dashboard data."));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export const refreshROIDashboardDataAction =
  (): AppThunk => async (dispatch: AppDispatch) => {
    dispatch(setRefreshing(true));
    dispatch(setError(null));
    try {
      await refreshROIDashboardData();
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to refresh ROI dashboard data."));
      }
      throw error;
    } finally {
      dispatch(setRefreshing(false));
    }
  };

export default roiDashboardSlice.reducer;
